import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";
import { TrashBinIcon, ListIcon } from "../../icons";
import { LoaderSpinner } from "../Loader";
import { Link } from "react-router";
import Button from "../ui/button/Button";
import { ReactNode } from "react";

export interface ColumnConfig<T> {
  key: keyof T;
  header: string;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
}

export interface ActionConfig<T> {
  label: ReactNode;
  onClick: (item: T) => void;
  variant?: "primary" | "outline";
  size?: "sm" | "md";
  className?: string | ((item: T) => string);
  title?: string;
  disabled?: boolean;
}

interface CommonTableProps<T> {
  data: T[];
  columns: ColumnConfig<T>[];
  onDelete?: (id: string) => Promise<void>;
  deleteBtnSize?: "sm" | "md";
  statusLink?: (id: string) => string;
  isLoading: boolean;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  showPagination?: boolean;
  actions?: ActionConfig<T>[];
}

const CommonTable = <T extends { id: string | number }>({
  data,
  columns,
  onDelete,
  deleteBtnSize = "sm",
  statusLink,
  isLoading,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  showPagination = false,
  actions = [],
}: CommonTableProps<T>) => {
  const handleDelete = async (id: string) => {
    const confirmation = window.confirm(
      "Are you sure you want to delete this?"
    );
    if (!confirmation) return;
    await onDelete?.(id);
  };

  const renderPagination = () => {
    if (!showPagination || totalPages <= 1) return null;

    const maxVisiblePages = 5;
    const pages = [];

    // Always show first page
    pages.push(
      <Button
        key="first"
        size="sm"
        variant="outline"
        onClick={() => onPageChange?.(1)}
      >
        First
      </Button>
    );

    // Previous page button
    pages.push(
      <Button
        key="prev"
        size="sm"
        variant="outline"
        onClick={() => onPageChange?.(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Previous
      </Button>
    );

    // Calculate range of visible pages
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add ellipsis before if needed
    if (startPage > 1) {
      pages.push(
        <span key="start-ellipsis" className="px-2">
          ...
        </span>
      );
    }

    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          variant={currentPage === i ? "primary" : "outline"}
          onClick={() => onPageChange?.(i)}
        >
          {i}
        </Button>
      );
    }

    // Add ellipsis after if needed
    if (endPage < totalPages) {
      pages.push(
        <span key="end-ellipsis" className="px-2">
          ...
        </span>
      );
    }

    // Next page button
    pages.push(
      <Button
        key="next"
        size="sm"
        variant="outline"
        onClick={() => onPageChange?.(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
      </Button>
    );

    // Always show last page
    pages.push(
      <Button
        key="last"
        size="sm"
        variant="outline"
        onClick={() => onPageChange?.(totalPages)}
      >
        Last
      </Button>
    );

    return (
      <div className="flex justify-center mt-4 gap-2 items-center">{pages}</div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        <div className="max-w-full overflow-x-auto">
          <div className="min-w-[1102px]">
            <Table>
              {/* Table Header */}
              <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key as string}
                      isHeader
                      className={`px-4 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 ${
                        column.className || ""
                      }`}
                    >
                      {column.header}
                    </TableCell>
                  ))}
                  {(actions.length > 0 || onDelete || statusLink) && (
                    <TableCell
                      isHeader
                      className="px-4 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>

              {/* Table Body */}
              <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1}>
                      <div className="flex justify-center p-5">
                        <LoaderSpinner />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {data?.length > 0 ? (
                      data.map((item, index) => (
                        <TableRow key={item.id}>
                          {columns.map((column) => (
                            <TableCell
                              key={column.key as string}
                              className={`px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400 ${
                                column.className || ""
                              }`}
                            >
                              {column.render
                                ? column.render(item, index)
                                : String(item[column.key])}
                            </TableCell>
                          ))}
                          <TableCell className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400 flex gap-2 items-center">
                            {actions.map((action, index) => (
                              <Button
                                key={index}
                                size={action.size || "sm"}
                                variant={action.variant || "outline"}
                                onClick={
                                  action.disabled
                                    ? undefined
                                    : () => action.onClick(item)
                                }
                                className={
                                  typeof action.className === "function"
                                    ? action.className(item)
                                    : action.className
                                }
                                title={action.title}
                                disabled={action.disabled}
                              >
                                {action.label}
                              </Button>
                            ))}
                            {onDelete && (
                              <Button
                                onClick={() => handleDelete(item.id.toString())}
                                variant="outline"
                                size={deleteBtnSize}
                              >
                                <TrashBinIcon className="text-red-400 hover:cursor-pointer" />
                              </Button>
                            )}
                            {statusLink && (
                              <Link
                                to={statusLink(item.id.toString())}
                                className="hover:cursor-pointer"
                              >
                                <ListIcon
                                  className="text-blue-500"
                                  title="View Statuses"
                                />
                              </Link>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={columns.length + 1}>
                          <p className="text-center text-gray-500 text-theme-sm dark:text-gray-400 p-4">
                            No data
                          </p>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
      {renderPagination()}
    </div>
  );
};

export default CommonTable;
